/**
 * The document an untrusted HTML email body is shown in (F20-c spec FR-I3; F20-c WI-1.3 plan
 * Decisions 9, 11 and 12). `SafeMessageBody` puts the result in an `<iframe srcdoc>` whose sandbox
 * grants no script, no form and no top navigation; this function adds what the sandbox cannot:
 *
 *  - **A Content-Security-Policy**, first in `<head>`, so nothing remote loads — no image (a tracking
 *    pixel), stylesheet, font or media; only `data:` images and inline styles.
 *  - **The link rule — the only control for links.** The frame grants `allow-popups-to-escape-sandbox`,
 *    so a link opens a window that is NOT sandboxed. A link address is kept only when its scheme is
 *    `http`, `https` or `mailto` (the F20-c FR-S8 allow-list, the same test as the editor's `isAllowedUri`);
 *    any other (`javascript:`, `data:`, `vbscript:`, an operating-system handler such as `ms-msdt:`,
 *    a relative address) is removed and the link's text kept. A kept link opens in a new tab with no
 *    opener and no referrer. This rule is load-bearing: change it only as a reviewed decision
 *    (forge-et-fe `CLAUDE.md` § Conventions, the untrusted-HTML rule).
 *  - **Element removal, as defence in depth:** `script`, `iframe`, `frame`, `object`, `embed`, `link`,
 *    `base`, every `meta` of the email's own, and the SVG animation elements `set`, `animate` and
 *    `animateTransform` (each can rewrite an `href` or `target` after this function has run).
 *  - **No shadow root:** every `template` is removed with its content, and every `shadowroot…`
 *    attribute anywhere. `DOMParser` does not attach a declarative shadow root
 *    (`<template shadowrootmode>`), so its content is out of reach of the two rules above, while the
 *    frame's own parser would attach it and a link inside would keep any address and any `target`.
 *  - **A second parse, as defence in depth:** the finished document is parsed again, as the frame will
 *    parse it, and checked for anything the rules above remove. HTML can parse differently the second
 *    time (a nested `form` inside MathML is one known case); if anything disallowed appears, the body
 *    is shown as plain text instead.
 *
 * `DOMParser` builds a document with no browsing context, so nothing in it runs or loads while it is
 * read here.
 *
 * **A future CRM Content-Security-Policy applies inside the frame too** (an `about:srcdoc` document
 * inherits its parent's policy). It must keep `style-src 'unsafe-inline'` and `img-src data:` and must
 * not block the frame, or every HTML body loses its styling or renders blank — tracked in harness
 * `.forge/cloud-gated-validation.md` § B, F20-c (WI-1.1's block, item (e)).
 */

/** The frame document's own policy. Exported so the tests assert the exact string. */
export const MESSAGE_FRAME_CSP =
    "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src 'none'; media-src 'none'; form-action 'none'";

/**
 * Appended last, after every style of the email's own: a body sized from the frame (`height: 100%`
 * plus a margin) would otherwise grow the frame on every measurement (F20-c WI-1.3 plan Decision 11).
 */
export const HEIGHT_GUARD_STYLE = 'html, body { height: auto !important; min-height: 0 !important; }';

/** The elements removed from every body, compared by lower-cased local name, in any namespace. */
const REMOVED_ELEMENTS: ReadonlySet<string> = new Set([
    'script',
    'iframe',
    'frame',
    'object',
    'embed',
    'link',
    'base',
    'meta',
    'set',
    'animate',
    'animatetransform',
    'template',
]);

/** The declarative-shadow-root attributes: `shadowrootmode`, `shadowrootclonable`, the older `shadowroot`, and the rest. */
const SHADOW_ROOT_ATTRIBUTE = /^shadowroot/i;

/** The F20-c FR-S8 allow-list: the scheme of a kept address, after trimming and ignoring case. */
const ALLOWED_LINK = /^(https?:|mailto:)/i;

/** True when `href` may stay on a link. */
export function isAllowedLink(href: string): boolean {
    return ALLOWED_LINK.test(href.trim());
}

function removeElements(root: Document): void {
    for (const element of Array.from(root.querySelectorAll('*'))) {
        if (REMOVED_ELEMENTS.has(element.localName.toLowerCase())) {
            element.remove();
            continue;
        }
        for (const attribute of Array.from(element.attributes)) {
            if (SHADOW_ROOT_ATTRIBUTE.test(attribute.name)) {
                element.removeAttributeNode(attribute);
            }
        }
    }
}

/**
 * Apply the link rule to every element carrying `href` or `xlink:href` (HTML `a` and `area`, SVG `a`,
 * any namespace): keep an allowed address and open it in a new tab with no opener and no referrer;
 * remove any other, keeping the element and its text. Exported because `SafeMessageBody` applies it
 * again to the frame's live document once it has loaded.
 */
export function applyLinkRule(root: Document): void {
    for (const element of Array.from(root.querySelectorAll('*'))) {
        const hrefs = Array.from(element.attributes).filter((attribute) => attribute.localName === 'href');
        if (hrefs.length === 0) {
            continue;
        }
        let kept = false;
        for (const attribute of hrefs) {
            if (isAllowedLink(attribute.value)) {
                kept = true;
            } else {
                element.removeAttributeNode(attribute);
            }
        }
        if (kept) {
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noopener noreferrer');
        } else {
            element.removeAttribute('target');
        }
    }
}

/**
 * Remove the elements `REMOVED_ELEMENTS` names that can change a document after it is built (the SVG
 * animation elements). Exported for the second pass `SafeMessageBody` makes on the loaded frame.
 */
export function removeAnimationElements(root: Document): void {
    for (const element of Array.from(root.querySelectorAll('*'))) {
        const name = element.localName.toLowerCase();
        if (name === 'set' || name === 'animate' || name === 'animatetransform') {
            element.remove();
        }
    }
}

/**
 * Remove every element that hosts a shadow root, with its content. Exported for the second pass
 * `SafeMessageBody` makes on the loaded frame, where a declarative shadow root that reached the frame
 * would be attached. Only an open root can be seen from outside; `safeSrcDoc` removing every
 * `template` is what keeps a closed one out.
 */
export function removeShadowHosts(root: Document): void {
    for (const element of Array.from(root.querySelectorAll('*'))) {
        if (element.shadowRoot !== null) {
            element.remove();
        }
    }
}

/** True when `element` is one of the two metas `withFrameHead` puts first in `<head>`. */
function isFrameMeta(doc: Document, element: Element): boolean {
    const [first, second] = Array.from(doc.head.children);
    if (element === first) {
        return (
            element.localName === 'meta' &&
            element.getAttribute('http-equiv') === 'Content-Security-Policy' &&
            element.getAttribute('content') === MESSAGE_FRAME_CSP
        );
    }
    return element === second && element.localName === 'meta' && element.getAttribute('name') === 'referrer';
}

/**
 * True when `doc` holds something the rules remove: a removed element other than the two frame metas,
 * a `shadowroot…` attribute, a link address outside the allow-list, or a kept link that does not open
 * in a new tab. Also true when the CSP meta is not first in `<head>`.
 */
function holdsDisallowed(doc: Document): boolean {
    const first = doc.head.firstElementChild;
    if (first === null || !isFrameMeta(doc, first)) {
        return true;
    }
    for (const element of Array.from(doc.querySelectorAll('*'))) {
        if (REMOVED_ELEMENTS.has(element.localName.toLowerCase()) && !isFrameMeta(doc, element)) {
            return true;
        }
        const attributes = Array.from(element.attributes);
        if (attributes.some((attribute) => SHADOW_ROOT_ATTRIBUTE.test(attribute.name))) {
            return true;
        }
        const hrefs = attributes.filter((attribute) => attribute.localName === 'href');
        if (hrefs.some((attribute) => !isAllowedLink(attribute.value))) {
            return true;
        }
        if (hrefs.length > 0 && element.getAttribute('target') !== '_blank') {
            return true;
        }
    }
    return false;
}

/**
 * Put the frame's head into `doc` (the CSP meta first, then the referrer meta, then `frameStyle`),
 * append the height guard style last, and serialise.
 */
function withFrameHead(doc: Document, frameStyle: string): string {
    const csp = doc.createElement('meta');
    csp.setAttribute('http-equiv', 'Content-Security-Policy');
    csp.setAttribute('content', MESSAGE_FRAME_CSP);
    const referrer = doc.createElement('meta');
    referrer.setAttribute('name', 'referrer');
    referrer.setAttribute('content', 'no-referrer');
    const base = doc.createElement('style');
    base.textContent = frameStyle;
    doc.head.prepend(csp, referrer, base);

    const guard = doc.createElement('style');
    guard.textContent = HEIGHT_GUARD_STYLE;
    doc.body.append(guard);

    return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
}

/**
 * The fallback: the frame's head and, as the body, the cleaned body's text as one text node (its
 * `style` elements, the height guard among them, left out), so no element of the email's own reaches
 * the frame.
 */
function plainTextSrcDoc(cleaned: Document, frameStyle: string): string {
    for (const style of Array.from(cleaned.body.querySelectorAll('style'))) {
        style.remove();
    }
    const doc = new DOMParser().parseFromString('', 'text/html');
    doc.body.append(doc.createTextNode(cleaned.body.textContent ?? ''));
    return withFrameHead(doc, frameStyle);
}

/**
 * The `srcdoc` for `html`: the CSP meta first in `<head>`, then the referrer meta, then `frameStyle`
 * (the CRM's base text style, which the email's own styles follow and may override), the body with
 * the elements removed and the link rule applied, and the height guard style last. When that
 * document, parsed again, holds anything the rules remove, the body's plain text instead.
 */
export function safeSrcDoc(html: string, frameStyle: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    removeElements(doc);
    applyLinkRule(doc);

    const result = withFrameHead(doc, frameStyle);
    if (!holdsDisallowed(new DOMParser().parseFromString(result, 'text/html'))) {
        return result;
    }
    return plainTextSrcDoc(doc, frameStyle);
}
